import { buildPreContractRoofDesignSnapshot } from './preContractRoofDesign';
import { resolvePreContractSolarMetrics } from './preContractSolarDesignMetrics';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';
import type { PreContractPpwMetricsOverride } from './preContractPpwPricing';
import type { PreContractSummaryItem } from '../components/PreContractModuleStep.types';
import type { RetrieveSystemDesignResponse } from '../../services/oneButtonServices/designServices';
import type { PriceQuoteOverrides } from '../../store/oneButtonStore';

function metricTextReady(value: string | null | undefined): boolean {
    const text = String(value ?? '').trim();
    if (!text || text === '—' || text === '-' || text.toUpperCase() === 'N/A') return false;
    if (/loading/i.test(text)) return false;
    return /[1-9]/.test(text);
}

function positiveNumber(value: unknown): boolean {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) && n > 0;
}

function positiveRate(raw: string | number | null | undefined): boolean {
    if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0;
    const n = Number(String(raw ?? '').replace(/[$,\s]/g, '').trim());
    return Number.isFinite(n) && n > 0;
}

function joinMissing(fields: string[]): string {
    if (fields.length === 1) return fields[0];
    if (fields.length === 2) return `${fields[0]} and ${fields[1]}`;
    return `${fields.slice(0, -1).join(', ')}, and ${fields[fields.length - 1]}`;
}

export function solarQuoteDesignBlockReason(input: {
    form: PreContractIntakeForm;
    systemDesign: unknown;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    summaryItems?: PreContractSummaryItem[];
    ppaRateLoading?: boolean;
}): string | null {
    const metrics = resolvePreContractSolarMetrics({
        form: input.form,
        systemDesign: input.systemDesign,
        metricsOverride: input.metricsOverride,
    });
    const productionFallback =
        input.summaryItems?.find((item) => item.label === 'System Production')?.value ?? '';
    const missing: string[] = [];
    if (!positiveNumber(metrics.systemKw)) missing.push('system size');
    if (!positiveNumber(metrics.annualProductionKwh) && !metricTextReady(productionFallback)) {
        missing.push('production');
    }
    if (!positiveNumber(metrics.multiplier)) missing.push('multiplier');
    if (!positiveRate(input.form.utilityRate)) missing.push('all-in rate');
    if (input.ppaRateLoading || !positiveRate(input.form.targetMinPpaRate)) {
        missing.push('target/min PPA rate');
    }
    if (missing.length === 0) return null;
    return `Quote steps unlock when ${joinMissing(missing)} ${
        missing.length === 1 ? 'is' : 'are'
    } on the Solar card.`;
}

export function roofQuoteDesignBlockReason(input: {
    form: PreContractIntakeForm;
    systemDesign?: RetrieveSystemDesignResponse | null;
    priceQuoteOverrides?: PriceQuoteOverrides | null;
}): string | null {
    const snapshot = buildPreContractRoofDesignSnapshot(input);
    const missing: string[] = [];
    if (!(snapshot.total > 0)) missing.push('roof squares');
    if (!(snapshot.pricePerSqft != null && snapshot.pricePerSqft > 0)) missing.push('price / sq ft');
    if (!snapshot.shingleColor) missing.push('shingle');
    if (missing.length === 0) return null;
    return `Quote steps unlock when ${joinMissing(missing)} ${
        missing.length === 1 ? 'is' : 'are'
    } on the Roof card.`;
}

export function batteryQuoteDesignBlockReason(form: PreContractIntakeForm): string | null {
    const missing: string[] = [];
    if (!(form.batteryQuantity > 0)) missing.push('count');
    if (!form.batteryName.trim() || form.batteryName.trim() === 'Not selected') {
        missing.push('model');
    }
    if (missing.length === 0) return null;
    return `Quote steps unlock when ${joinMissing(missing)} ${
        missing.length === 1 ? 'is' : 'are'
    } on the Battery card.`;
}

export function productQuoteDesignBlockReason(
    product: PreContractProduct,
    input: {
        form: PreContractIntakeForm;
        systemDesign?: RetrieveSystemDesignResponse | null;
        priceQuoteOverrides?: PriceQuoteOverrides | null;
        metricsOverride?: PreContractPpwMetricsOverride | null;
        summaryItems?: PreContractSummaryItem[];
        ppaRateLoading?: boolean;
    },
): string | null {
    if (product === 'Solar') {
        return solarQuoteDesignBlockReason({
            form: input.form,
            systemDesign: input.systemDesign,
            metricsOverride: input.metricsOverride,
            summaryItems: input.summaryItems,
            ppaRateLoading: input.ppaRateLoading,
        });
    }
    if (product === 'Roof') {
        return roofQuoteDesignBlockReason({
            form: input.form,
            systemDesign: input.systemDesign,
            priceQuoteOverrides: input.priceQuoteOverrides,
        });
    }
    if (product === 'Battery') {
        return batteryQuoteDesignBlockReason(input.form);
    }
    return null;
}
